import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components/macro';
import { ProductCardPropsV2 } from '../types/ProductCardProps';
import { parseBoolean } from '../utils/parseBoolean';
import IconButton from './IconButton';
import PriceDisplay from './PriceDisplay';
import ProductVariationSelect from './ProductVariationSelect';
import { ProductBuyButtonsV2 } from './ProductBuyButtonsV2';
import { useProductViewEvent } from '../utils/hooks/useProductViewEvent';
import { useProductActionsState } from '../utils/hooks/useProductActionsState';

const UnstyledProductCardV2: React.FC<ProductCardPropsV2> = observer(
  (props) => {
    const {
      className,
      product,
      priceFormat,
      enablevariations,
      format,
      hideBuyControls,
      overrideQuantity,
      variationsort,
      headerIcons,
      trybutton,
      likebutton,
    } = props;
    const { state, increment, decrement, fallbackImage } =
      useProductActionsState(props);
    useProductViewEvent(state.selectedVariation);

    return state.selectedVariation ? (
      <div
        className={`ww-product-card ${className ?? ''} ${format ?? ''}`}
        data-product-id={product?.id}
      >
        {!headerIcons || headerIcons === 'product-action' ? (
          <div className="ww-product-card-header">
            {parseBoolean(trybutton?.show, true) && state.data?.isWine && (
              <IconButton
                solid={state.isTried}
                primary
                onClick={state.tried}
                aria-label={state.isTried ? 'un-try' : 'tried'}
                className="ww-product-try-button"
              >
                <i className="icon-ok" />
              </IconButton>
            )}
            {parseBoolean(likebutton?.show, true) && state.data?.isWine && (
              <IconButton
                solid={state.isLiked}
                primary
                onClick={state.liked}
                aria-label={state.isTried ? 'un-like' : 'liked'}
                className="ww-product-like-button"
              >
                <i className="icon-heart" />
              </IconButton>
            )}
          </div>
        ) : (
          <div className="ww-product-card-header">
            <IconButton primary onClick={state.removeItem} aria-label="remove">
              <i className="icon-cancel" />
            </IconButton>
          </div>
        )}

        <div className="ww-product-card-content">
          <div className="ww-product-image">
            <a href={state.selectedVariation.link}>
              <img
                src={state.image}
                onError={fallbackImage}
                alt={state.selectedVariation.name}
              />
            </a>
          </div>
          <div className="ww-product-content">
            <small className="ww-product-winery">
              {state.selectedVariation.brandNameOverride ||
                state.selectedVariation.brand?.name}
            </small>
            <h3 className="ww-product-name">
              <a href={state.selectedVariation.link}>
                {state.selectedVariation.name}
              </a>
            </h3>
            <span className="ww-product-subtitle">
              {state.selectedVariation.vintage &&
                parseInt(state.selectedVariation.vintage) &&
                parseInt(state.selectedVariation.vintage) > 0 && (
                  <small className="ww-product-vintage">
                    {state.selectedVariation.vintage}{' '}
                  </small>
                )}
              {(state.selectedVariation.region ||
                state.selectedVariation.varietyType) && (
                <small className="ww-product-variety">
                  {state.selectedVariation.varietyType}{' '}
                </small>
              )}
              {(state.selectedVariation.region ||
                state.selectedVariation.state) && (
                <small className="ww-product-location">
                  {[
                    state.selectedVariation.region,
                    state.selectedVariation.state,
                  ]
                    .filter((item) => !!item && item !== 'Multiple')
                    .join(', ')}
                </small>
              )}
            </span>
          </div>
        </div>
        {state.selectedVariation.isSaleable &&
          state.selectedVariation.websiteProductStock
            ?.displayOutOfStockMessage && (
            <div className="ww-out-of-stock-info">
              {state.selectedVariation.websiteProductStock?.outOfStockMessage ??
                'Out of stock'}
            </div>
          )}
        <div className="ww-product-card-footer">
          {state.selectedVariation && (
            <PriceDisplay
              data={state.selectedVariation}
              priceFormat={priceFormat ?? { display: 'minimumUnitPrice' }}
              hidePricePrefix
              hidePriceSuffix
            />
          )}
          {state.data?.isSaleable && <span className="divider">/</span>}
          {state.data &&
          parseBoolean(enablevariations, true) &&
          state.data.variations &&
          state.data.variations.length ? (
            <ProductVariationSelect
              data={state.data}
              variationsort={variationsort}
            />
          ) : !state.data ? (
            ''
          ) : (
            state.data?.isSaleable && (
              <span className="ww-price-suffix">
                {priceFormat?.suffix || state.priceSuffix}
              </span>
            )
          )}
          <div className="spacer" />
          {!hideBuyControls && state.data?.isSaleable && (
            <ProductBuyButtonsV2
              cartQuantity={overrideQuantity ?? state.cartQuantity}
              onDecrementClick={decrement}
              onIncrementClick={increment}
              minimumUnitPurchase={state.data.minimumUnitPurchase}
            />
          )}
        </div>
      </div>
    ) : null;
  },
);

export const ProductCardV2 = styled((props: ProductCardPropsV2) => (
  <>
    <UnstyledProductCardV2 {...props}></UnstyledProductCardV2>
  </>
))`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--white);
  border-radius: 1.231em;
  box-shadow: 0 0.5em 1em rgba(0, 0, 0, 0.1);
  /* width: 31.923em; */
  min-width: 23.538em;
  color: var(--black);

  &.store {
    flex: none;
    flex-basis: 32%;
  }

  .spacer {
    flex: auto;
  }

  .ww-product-card-header {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 0.769em;
    .ww-icon-button {
      flex: none;
      margin-left: 0.769em;
    }
  }

  .ww-product-card-content {
    display: flex;
    padding: 1.538em 0;

    .ww-product-image {
      flex: none;
      width: 6.846em;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      overflow: hidden;
      img {
        height: 6.923em;
        flex: none;
      }
    }

    .ww-product-content {
      min-height: 3em;
      display: flex;
      flex-direction: column;
      padding-right: 1.538em;

      .ww-product-winery {
        margin-right: 6.154em;
        font-size: 1.077em;
      }

      .ww-product-name {
        margin: 0;
        margin-right: 4em;
        font-size: 1.385em;
        font-weight: bold;

        a {
          color: inherit;
          text-decoration: none;
        }
      }

      .ww-product-location,
      .ww-product-vintage,
      .ww-product-variety {
        font-size: 1.077em;
        color: var(--gray-4);
      }
    }
  }

  &.store .ww-product-card-content {
    flex: auto;
    flex-direction: column-reverse;
    padding: 1.538em;

    .ww-product-image {
      flex: auto;
      margin-top: 1.538em;
      width: 100%;
      align-items: flex-end;
      justify-content: center;
      img {
        height: 15.385em;
      }
    }
  }

  .ww-out-of-stock-info {
    margin: -1.538em 1.325em 0 1.325em;
    min-height: -1.538em;
    color: var(--warn-color);
  }

  .ww-product-card-footer {
    border-top: 1px solid var(--gray-2);
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0.769em;
    min-height: 4.35em;

    .price-info,
    .quantity {
      font-size: 1.385em;
      font-weight: bold;
      font-family: inherit;
      flex: none;
      margin-left: 0.556em;
    }

    .divider {
      font-size: 1.538em;
      color: var(--gray-5);
      margin: 0 0.2em;
      flex: none;
    }

    .variation-select {
      appearance: none;
      padding: 0 0.25em;
      flex: 1 1 auto;
      border: none;
      height: 1.538em;
      display: flex;
      align-items: center;
      font-family: inherit;
      background-color: var(--gray-2);
      border-radius: 0.154em;
      font-size: 1.077em;
      width: initial;
      min-width: 10px;
      background-image: none;
      line-height: 1.7em;
    }

    .ww-icon-button {
      flex: none;
      margin-left: 0.769em;
    }
  }
`;

export default ProductCardV2;
